import { useState } from "react";
import { Button, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import DrWeedyLogo from "../assets/DrWeedyLogo";
import VslSection from "../components/VslSection";
import PricingSection from "../components/PricingSection";
import QASimulator from "../components/QASimulator";
import HowItWorksSection from "../components/HowItWorksSection";
import AgentsShowcaseSection from "../components/AgentsShowcaseSection";
import FeaturesSection from "../components/FeaturesSection";
import ComparisonSection from "../components/ComparisonSection";
import TargetAudienceSection from "../components/TargetAudienceSection";
import TestimonialsSection from "../components/TestimonialsSection";
import StatsSection from "../components/StatsSection";
import FAQSection from "../components/FAQSection";
import FinalCtaSection from "../components/FinalCtaSection";
import WaitlistDialog from "../components/WaitlistDialog";
import { useSalesOpen } from "../hooks/useSalesOpen";

const navLinks = [
  { label: "Como funciona", href: "#como-funciona" },
  { label: "Especialistas", href: "#especialistas" },
  { label: "Funcionalidades", href: "#funcionalidades" },
  { label: "Planos", href: "#planos" },
];

const stats = [
  { value: "18k+", label: "artigos indexados" },
  { value: "10k+", label: "consultas realizadas" },
  { value: "< 3s", label: "tempo de resposta" },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const salesClosed = useSalesOpen() === false;
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  // CTA principal trial-forward, levando ao plano recomendado (anual) com trial.
  // Com a venda fechada, o mesmo CTA abre a lista de espera.
  const goToTrial = () =>
    salesClosed ? setWaitlistOpen(true) : navigate("/signup?plan=annual&trial=1");

  return (
    <div className="min-h-screen bg-gradient-to-br from-petrolio-dark via-[#0a1a21] to-[#04090a] relative overflow-x-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-[#FF8904]/15 via-transparent to-transparent pointer-events-none"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-petrolio/10 via-transparent to-transparent pointer-events-none"></div>

      <header className="relative z-20 flex items-center justify-between px-6 md:px-12 py-5">
        <div className="flex items-center gap-3" data-test='logo-dr-weedy'>
          <DrWeedyLogo width={40} height={40} />
          <Typography
            sx={{
              color: "#ffffff",
              fontWeight: 700,
              fontSize: "1.25rem",
              fontFamily: "Oxanium, sans-serif",
              letterSpacing: "-0.02em",
            }}
          >
            Dr. Weedy
          </Typography>
        </div>

        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-sm font-medium text-white/70 hover:text-white transition-colors"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Button
            onClick={() => navigate("/login")}
            variant="outlined"
            sx={{
              borderColor: "rgba(255,255,255,0.25)",
              color: "rgba(255,255,255,0.85)",
              borderRadius: "20px",
              textTransform: "none",
              fontSize: "15px",
              fontWeight: 600,
              padding: "8px 28px",
              transition: "all 0.3s ease",
              "&:hover": {
                borderColor: "#FF8904",
                color: "#FF8904",
                backgroundColor: "rgba(255, 137, 4, 0.08)",
              },
            }}
          >
            Entrar
          </Button>
          <Button
            onClick={goToTrial}
            variant="contained"
            sx={{
              display: { xs: "none", sm: "inline-flex" },
              background: "linear-gradient(135deg, #FF8904 0%, #E7000B 100%)",
              borderRadius: "20px",
              textTransform: "none",
              fontSize: "15px",
              fontWeight: 700,
              padding: "8px 24px",
              color: "#ffffff",
              boxShadow: "0 6px 18px rgba(255, 137, 4, 0.35)",
              "&:hover": {
                background: "linear-gradient(135deg, #E7000B 0%, #FF8904 100%)",
              },
            }}
          >
            {salesClosed ? "Lista de espera" : "Começar grátis"}
          </Button>
        </div>
      </header>

      <VslSection />

      <section className="relative z-10 px-6 md:px-12 pt-16 pb-16 md:pt-20 md:pb-20">
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto">
          <div className="relative mb-8">
            <div className="absolute inset-0 bg-gradient-to-r from-[#FF8904]/30 via-petrolio/20 to-[#FF8904]/30 rounded-full blur-3xl scale-150"></div>
            <div className="relative" data-test='dr-weedy-logo'>
              <DrWeedyLogo width={96} height={96} />
            </div>
          </div>
          <Typography
            variant="h2"
            sx={{
              color: "#ffffff",
              fontWeight: 800,
              fontFamily: "Oxanium, sans-serif",
              letterSpacing: "-0.03em",
              lineHeight: 1.15,
              fontSize: { xs: "2rem", md: "2.8rem", lg: "3.2rem" },
              marginBottom: "20px",
            }}
          >
            Seu segundo{" "}
            <Typography
              component="span"
              sx={{
                fontWeight: 800,
                fontFamily: "Oxanium, sans-serif",
                fontSize: "inherit",
                lineHeight: "inherit",
                letterSpacing: "inherit",
                background: "linear-gradient(135deg, #FF8904 0%, #E7000B 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              cérebro clínico
            </Typography>{" "}
            para Cannabis.
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.7)",
              fontSize: { xs: "1.05rem", md: "1.25rem" },
              lineHeight: 1.7,
              maxWidth: "620px",
              marginBottom: "36px",
            }}
          >
            Enquanto você atende pacientes, o Dr. Weedy pesquisa literatura
            científica, analisa protocolos, verifica interações medicamentosas
            e organiza todo o raciocínio clínico em segundos. Cada consulta
            invoca múltiplos agentes especializados trabalhando em paralelo.
          </Typography>

          <div className="flex flex-col sm:flex-row items-center gap-3 mb-10">
            <Button
              onClick={goToTrial}
              variant="contained"
              sx={{
                background: "linear-gradient(135deg, #FF8904 0%, #E7000B 100%)",
                borderRadius: "24px",
                textTransform: "none",
                fontSize: "18px",
                fontWeight: 700,
                padding: "16px 44px",
                color: "#ffffff",
                border: "2px solid rgba(255, 137, 4, 0.3)",
                boxShadow: "0 8px 24px rgba(255, 137, 4, 0.4)",
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  transform: "translateY(-3px)",
                  boxShadow: "0 12px 32px rgba(255, 137, 4, 0.5)",
                  background: "linear-gradient(135deg, #E7000B 0%, #FF8904 100%)",
                },
              }}
            >
              {salesClosed ? "Entrar na lista de espera →" : "Começar teste gratuito →"}
            </Button>
            <Button
              component="a"
              href="#demonstracao"
              variant="outlined"
              sx={{
                borderRadius: "24px",
                textTransform: "none",
                fontSize: "18px",
                fontWeight: 700,
                padding: "16px 40px",
                color: "#ffffff",
                borderColor: "rgba(255,255,255,0.25)",
                "&:hover": {
                  borderColor: "#FF8904",
                  backgroundColor: "rgba(255, 137, 4, 0.08)",
                },
              }}
            >
              ▷ Ver demonstração
            </Button>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3">
            {stats.map((stat) => (
              <div key={stat.label} className="flex items-baseline gap-2">
                <Typography
                  sx={{
                    color: "#ffffff",
                    fontWeight: 800,
                    fontFamily: "Oxanium, sans-serif",
                    fontSize: "1.4rem",
                  }}
                >
                  {stat.value}
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.9rem" }}>
                  {stat.label}
                </Typography>
              </div>
            ))}
          </div>
        </div>
      </section>

      <HowItWorksSection />

      <AgentsShowcaseSection />

      <QASimulator />

      <FeaturesSection />

      <ComparisonSection />

      <TargetAudienceSection />

      <StatsSection />

      <TestimonialsSection />

      <PricingSection />

      <FAQSection salesClosed={salesClosed} />

      <FinalCtaSection salesClosed={salesClosed} onCta={goToTrial} />
      <WaitlistDialog open={waitlistOpen} onClose={() => setWaitlistOpen(false)} />

      <footer className="relative z-10 border-t border-white/10 px-6 md:px-12 py-14">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_1fr] gap-10">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <DrWeedyLogo width={32} height={32} />
              <Typography
                sx={{
                  color: "#ffffff",
                  fontWeight: 700,
                  fontSize: "1.1rem",
                  fontFamily: "Oxanium, sans-serif",
                }}
              >
                Dr. Weedy
              </Typography>
            </div>
            <Typography
              sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.9rem", lineHeight: 1.6, maxWidth: "320px" }}
            >
              Inteligência artificial especializada em medicina canabinoide,
              criada exclusivamente para médicos prescritores brasileiros.
            </Typography>
          </div>

          <FooterColumn
            title="Produto"
            links={[
              { label: "Como funciona", href: "#como-funciona" },
              { label: "Especialistas IA", href: "#especialistas" },
              { label: "Funcionalidades", href: "#funcionalidades" },
              { label: "Planos e preços", href: "#planos" },
            ]}
          />

          {/* "Empresa" e "Legal": páginas ainda não existem no app — sem destino
              real ainda, então os rótulos ficam sem link em vez de apontar pra
              lugar nenhum. Contato é a exceção, já tem um canal real (e-mail). */}
          <FooterColumn
            title="Empresa"
            links={[
              { label: "Sobre nós" },
              { label: "Blog" },
              { label: "Carreiras" },
              { label: "Contato", href: "mailto:weedy@saluton.clinic" },
            ]}
          />

          <FooterColumn
            title="Legal"
            links={[
              { label: "Termos de uso" },
              { label: "Privacidade" },
              { label: "LGPD" },
              { label: "Documentação" },
            ]}
          />
        </div>

        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 mt-12 pt-8 border-t border-white/5">
          <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.85rem" }}>
            © {new Date().getFullYear()} Dr. Weedy por Saluton Telemed. Todos os direitos reservados.
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.85rem" }}>
            Exclusivo para médicos habilitados · Não substitui julgamento clínico
          </Typography>
          <img
            src="/saluton-dark-horizontal.png"
            alt="Saluton Telemed"
            className="h-10 w-auto opacity-50"
          />
        </div>
      </footer>
    </div>
  );
}

interface FooterLink {
  label: string;
  href?: string;
}

function FooterColumn({ title, links }: { title: string; links: FooterLink[] }) {
  return (
    <div>
      <Typography
        sx={{
          color: "rgba(255,255,255,0.4)",
          fontWeight: 700,
          fontSize: "0.75rem",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          marginBottom: "16px",
        }}
      >
        {title}
      </Typography>
      <div className="flex flex-col gap-3">
        {links.map((link) =>
          link.href ? (
            <Typography
              key={link.label}
              component="a"
              href={link.href}
              sx={{
                color: "rgba(255,255,255,0.6)",
                fontSize: "0.9rem",
                textDecoration: "none",
                width: "fit-content",
                "&:hover": { color: "#FF8904" },
              }}
            >
              {link.label}
            </Typography>
          ) : (
            <Typography key={link.label} sx={{ color: "rgba(255,255,255,0.3)", fontSize: "0.9rem" }}>
              {link.label}
            </Typography>
          ),
        )}
      </div>
    </div>
  );
}
